import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Gift, Loader2, Minus, Plus, ShoppingBasket, Tag, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { celebrate } from "@/lib/celebration";
import { effectivePrice, isOnSale } from "@/lib/pricing";
import { useStore } from "@/context/StoreContext";

const USERNAME_RE = /^[A-Za-z0-9_]{3,16}$/;

export const BasketDrawer = () => {
  const { basket, basketOpen, setBasketOpen, setQty, removeFromBasket, clearBasket, total, username, platform, isGift, setIsGift, buyerUsername, setBuyerUsername } = useStore();
  const [phase, setPhase] = useState("cart");
  const [order, setOrder] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [giftMessage, setGiftMessage] = useState("");

  const discount = appliedCoupon ? Math.round(total * appliedCoupon.percent) / 100 : 0;
  const grandTotal = Math.round((total - discount) * 100) / 100;

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    try {
      const { data } = await api.get("/coupons/validate", { params: { code } });
      setAppliedCoupon(data);
      toast.success(`Coupon ${data.code} applied`, { description: `${data.percent}% off your order` });
    } catch (e) {
      toast.error("Invalid coupon", { description: formatApiError(e) });
    }
  };

  const close = (open) => {
    setBasketOpen(open);
    if (!open && phase === "success") {
      setPhase("cart");
      setOrder(null);
    }
  };

  const checkout = async () => {
    if (!username) {
      toast.error("Add a Minecraft username first", { description: "Click any package to set your username." });
      return;
    }
    if (isGift && !USERNAME_RE.test(buyerUsername.trim())) {
      toast.error("Enter your Minecraft username", { description: "Needed so we know who sent the gift." });
      return;
    }
    setPhase("processing");
    const payload = {
      username,
      platform,
      is_gift: isGift,
      buyer_username: isGift ? buyerUsername.trim() : null,
      gift_message: isGift ? giftMessage.trim() : null,
      coupon_code: appliedCoupon ? appliedCoupon.code : null,
      items: basket.map((i) => ({ package_id: i.id, qty: i.qty })),
    };
    try {
      const { data } = await api.post("/tebex/checkout", payload);
      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
        return;
      }
    } catch {
      // Tebex not configured or unavailable — fall back to demo checkout
    }
    try {
      const { data } = await api.post("/orders", payload);
      await new Promise((r) => setTimeout(r, 900));
      setOrder(data);
      setPhase("success");
      celebrate();
      clearBasket();
      setIsGift(false);
      setBuyerUsername("");
      setGiftMessage("");
      setAppliedCoupon(null);
      setCouponInput("");
    } catch (e) {
      setPhase("cart");
      toast.error("Checkout failed", { description: formatApiError(e) });
    }
  };

  return (
    <Sheet open={basketOpen} onOpenChange={close}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#0D0B12] border-l border-white/10 text-white flex flex-col" data-testid="basket-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl tracking-tight text-white flex items-center gap-2">
            <ShoppingBasket className="w-5 h-5 text-[#8B5CF6]" /> Your Basket
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            {username ? (
              <>Delivering to <span className="text-[#A78BFA] font-semibold" data-testid="basket-username">{username}</span>{platform === "bedrock" && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-widest bg-sky-400/20 text-sky-300" data-testid="basket-platform">BEDROCK</span>}</>
            ) : (
              "Add a package to begin."
            )}
          </SheetDescription>
        </SheetHeader>

        <AnimatePresence mode="wait">
          {phase === "success" && order ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex-1 flex flex-col items-center justify-center text-center gap-4 px-4"
              data-testid="checkout-success"
            >
              <CheckCircle2 className="w-16 h-16 text-[#8B5CF6]" strokeWidth={1.5} />
              <h3 className="font-display text-3xl tracking-tight">{order.is_gift ? "Gift sent" : "Order complete"}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Order <span className="font-mono text-[#A78BFA]">{order.order_id.slice(0, 8).toUpperCase()}</span> {order.is_gift ? (
                  <>is on its way to <span className="text-white font-semibold">{order.username}</span> as a gift.</>
                ) : (
                  <>for <span className="text-white font-semibold">{order.username}</span> is confirmed.</>
                )}{" "}
                Items arrive in-game within 5 minutes.
              </p>
              <p className="text-xs text-slate-600">Demo checkout — no payment was taken.</p>
              <button
                onClick={() => close(false)}
                data-testid="checkout-continue-button"
                className="mt-4 px-8 py-3 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-bold tracking-[0.15em] transition-[background-color] duration-300"
              >
                CONTINUE
              </button>
            </motion.div>
          ) : (
            <motion.div key="cart" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-1 flex flex-col mt-6 min-h-0">
              {basket.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-600 gap-3" data-testid="basket-empty">
                  <ShoppingBasket className="w-10 h-10" strokeWidth={1.5} />
                  <p className="text-sm">Your basket is empty.</p>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-0">
                    {basket.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10" data-testid={`basket-item-${item.id}`}>
                        <img src={item.image_url} alt={item.name} className="w-14 h-14 rounded-lg object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{item.name}</p>
                          <p className="text-xs font-mono">
                            {isOnSale(item) && (
                              <span className="line-through text-slate-500 mr-2">${item.price.toFixed(2)}</span>
                            )}
                            <span className="text-[#A78BFA]">${effectivePrice(item).toFixed(2)}</span>
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setQty(item.id, item.qty - 1)} data-testid={`basket-qty-minus-${item.id}`} className="p-1.5 rounded-md hover:bg-white/10 text-slate-400 hover:text-white transition-colors duration-200" aria-label="Decrease quantity">
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span className="w-6 text-center text-sm font-semibold" data-testid={`basket-qty-${item.id}`}>{item.qty}</span>
                          <button onClick={() => setQty(item.id, item.qty + 1)} data-testid={`basket-qty-plus-${item.id}`} className="p-1.5 rounded-md hover:bg-white/10 text-slate-400 hover:text-white transition-colors duration-200" aria-label="Increase quantity">
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <button onClick={() => removeFromBasket(item.id)} data-testid={`basket-remove-${item.id}`} className="p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-400/10 transition-colors duration-200" aria-label="Remove item">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4 mt-4 border-t border-white/10 space-y-4">
                    <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-sm text-slate-300">
                          <Gift className="w-4 h-4 text-[#8B5CF6]" /> This order is a gift
                        </span>
                        <Switch checked={isGift} onCheckedChange={setIsGift} data-testid="basket-gift-toggle" />
                      </div>
                      {isGift && (
                        <div className="space-y-1.5">
                          <Input
                            value={buyerUsername}
                            onChange={(e) => setBuyerUsername(e.target.value)}
                            placeholder="Your Minecraft username"
                            data-testid="basket-buyer-username-input"
                            className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]"
                          />
                          <p className="text-[11px] text-slate-500">Everything in the basket goes to <span className="text-[#A78BFA]">{username}</span> — from you.</p>
                          <Input
                            value={giftMessage}
                            onChange={(e) => setGiftMessage(e.target.value)}
                            maxLength={120}
                            placeholder="Gift message (optional)"
                            data-testid="basket-gift-message-input"
                            className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]"
                          />
                        </div>
                      )}
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 space-y-3">
                      <span className="flex items-center gap-2 text-sm text-slate-300">
                        <Tag className="w-4 h-4 text-[#8B5CF6]" /> Coupon code
                      </span>
                      {appliedCoupon ? (
                        <div className="flex items-center justify-between">
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/40 text-emerald-300 text-xs font-bold" data-testid="basket-coupon-applied">
                            {appliedCoupon.code} — {appliedCoupon.percent}% OFF
                          </span>
                          <button onClick={() => setAppliedCoupon(null)} data-testid="basket-coupon-remove" className="p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-400/10 transition-colors duration-200" aria-label="Remove coupon">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            value={couponInput}
                            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                            onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                            placeholder="e.g. SUMMER20"
                            data-testid="basket-coupon-input"
                            className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6] uppercase"
                          />
                          <button
                            onClick={applyCoupon}
                            data-testid="basket-coupon-apply-button"
                            className="px-4 rounded-lg bg-white/10 hover:bg-[#8B5CF6] text-white text-xs font-bold tracking-[0.15em] transition-[background-color] duration-300"
                          >
                            APPLY
                          </button>
                        </div>
                      )}
                    </div>
                    {appliedCoupon && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500 tracking-[0.15em]">SUBTOTAL</span>
                          <span className="text-slate-400 font-mono">${total.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm" data-testid="basket-discount-row">
                          <span className="text-emerald-400 tracking-[0.15em]">DISCOUNT ({appliedCoupon.code})</span>
                          <span className="text-emerald-400 font-mono">-${discount.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400 tracking-[0.15em]">TOTAL</span>
                      <span className="font-display text-2xl text-white" data-testid="basket-total">${grandTotal.toFixed(2)}</span>
                    </div>
                    <button
                      onClick={checkout}
                      disabled={phase === "processing"}
                      data-testid="basket-checkout-button"
                      className="w-full py-4 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-60 text-white text-sm font-bold tracking-[0.15em] shadow-[0_0_30px_rgba(139,92,246,0.35)] transition-[background-color,opacity] duration-300 flex items-center justify-center gap-2"
                    >
                      {phase === "processing" ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> PROCESSING…</>
                      ) : (
                        "CHECKOUT"
                      )}
                    </button>
                    <p className="text-[11px] text-center text-slate-600">Demo checkout — no real payment is processed.</p>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </SheetContent>
    </Sheet>
  );
};
